import axios from "axios";
import { supabase } from "./supabase";
import { humanizeAxiosError } from "./humanizeError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Phase 2F.12: in-memory access-token cache. The auth provider (lib/auth.jsx) updates this
// whenever Supabase session state changes, BEFORE any API call is made. The request
// interceptor reads synchronously from this cache, so the normal post-login path never
// touches `supabase.auth.getSession()` and therefore cannot hang on a locked SDK storage.
let _cachedAccessToken = null;
export function setCachedAccessToken(token) {
  _cachedAccessToken = token || null;
}

// Logout-race flag (lib/auth.jsx owns it). While an explicit sign-out is in progress, the
// Supabase session is already revoked but protected UI may still be unmounting, so in-flight
// requests can return 401 "Invalid token". We suppress ONLY the user-facing toast copy for those
// expected transition 401s — the request still fails and backend rejection is unchanged.
let _signingOut = false;
export function setSigningOut(v) {
  _signingOut = !!v;
}

// Bounded fallback: only used on cold-start API calls before the auth provider has had a
// chance to populate the cache. If `getSession()` hangs, we proceed without an Authorization
// header so the request fails cleanly (401) instead of stranding the UI forever.
const INTERCEPTOR_SESSION_TIMEOUT_MS = 1500;
async function _boundedGetSessionToken() {
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise((resolve) =>
        setTimeout(() => {
          console.warn(
            `[api] Interceptor supabase.auth.getSession() exceeded ${INTERCEPTOR_SESSION_TIMEOUT_MS}ms; ` +
              "sending request without Authorization header."
          );
          resolve({ data: { session: null }, __timedOut: true });
        }, INTERCEPTOR_SESSION_TIMEOUT_MS)
      ),
    ]);
    return result?.data?.session?.access_token || null;
  } catch (e) {
    console.warn("[api] Interceptor getSession() failed", e);
    return null;
  }
}

api.interceptors.request.use(async (config) => {
  let token = _cachedAccessToken;
  if (!token) {
    token = await _boundedGetSessionToken();
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Exported re-export kept for call sites that import from api.js. The pure implementation lives
// in ./humanizeError so the humanisation contract can be unit-tested without axios/supabase.
export { humanizeAxiosError };

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    err.code = (detail && typeof detail === "object") ? detail.code : undefined;
    // Logout-race suppression: ONLY when an explicit sign-out is in progress AND the status is 401,
    // drop the user-facing message so the expected transition 401 produces no toast. Every other
    // 401 (and all non-401 errors) keep their humanised displayMessage unchanged.
    if (_signingOut && err?.response?.status === 401) {
      err.displayMessage = null;
      err.silent = true;
    } else {
      err.displayMessage = humanizeAxiosError(err);
    }
    return Promise.reject(err);
  }
);
